import { useState } from 'react';
import { createStoryPageMessages } from './createStoryPage.messages';
import { fetchStoryTemplate } from '@/api/services/story-templates/fetchStoryTemplate';
import { createCharacters } from '@/api/services/characters/createCharacters';
import { fetchCharacters } from '@/api/services/characters/fetchCharacters';
import { createGeneratedStory } from '@/api/services/generated-stories/createGeneratedStory';
import type {
  StoryTemplate,
  CharacterWithUrls,
  CharacterAssignment,
  GeneratedStory,
  DedicationFormValues,
  CharacterSelection,
} from 'schemas';
import type { CharacterFormState } from 'schemas';
import { ApiError } from '@/api/ApiError';
import { UNKNOWN_ERROR_API_ERROR } from '@/api/helpers/unknownError';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { CharacterForm } from '@/forms/CharacterForm';
import { DedicationForm } from '@/forms/DedicationForm';
import { CharacterPreviewStep } from './components/CharacterPreview';
import { StoryPreviewStep } from './components/StoryPreviewStep';
import { Heading2, Small } from '@/components/Typography';
import { useLoadOnce } from '@/hooks/useLoadOnce';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { buildRoute } from '@/routes/buildRoute';

const TOTAL_STEPS = 4;

export function CreateStoryPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [storyTemplate, setStoryTemplate] = useState<StoryTemplate | null>(
    null
  );
  const [loadingTemplate, setLoadingTemplate] = useState(true);

  const [storyCharacters, setStoryCharacters] = useState<CharacterWithUrls[]>(
    []
  );
  const [existingCharacters, setExistingCharacters] = useState<
    CharacterWithUrls[]
  >([]);
  const [formData, setFormData] = useState<CharacterFormState[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedStory, setGeneratedStory] = useState<GeneratedStory | null>(
    null
  );

  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();

  // Load story template and existing characters on mount
  useLoadOnce(() => {
    const loadData = async () => {
      if (!templateId) {
        // TODO: Show error toast and redirect to templates page
        console.error('Template ID is missing');
        return;
      }
      try {
        // Load story template
        const template = await fetchStoryTemplate(templateId);
        setStoryTemplate(template);

        // Only fetch existing characters in development mode
        let characters: CharacterWithUrls[] = [];
        if (import.meta.env.DEV) {
          // only enable existing character selection in dev mode
          characters = await fetchCharacters();
        }
        setExistingCharacters(characters);

        // Initialize form data for each role with optional gender and age
        const initialFormData = template.story_template_roles.map(
          (_role, _index) => ({
            name: '',
            age: undefined,
            gender: undefined,
            reference_image: null,
          })
        );
        setFormData(initialFormData);
      } catch (err) {
        const apiError =
          err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
        toast.error(apiError.message);
      } finally {
        setLoadingTemplate(false);
      }
    };

    loadData();
  });

  const handleFormDataUpdate = (updatedFormData: CharacterFormState[]) => {
    setFormData(updatedFormData);
  };

  const handleCharacterSubmit = async (
    newCharacters: CharacterFormState[],
    characterSelections: CharacterSelection[]
  ) => {
    setIsSubmitting(true);

    try {
      // Get the current form data to determine which characters are existing vs new
      const allCharacters: CharacterWithUrls[] = [];

      // Process each character selection based on the actual selection state
      characterSelections.forEach((selection, _index) => {
        if (selection.type === 'existing' && selection.existingCharacterId) {
          // Find the existing character by ID
          const existingChar = existingCharacters.find(
            (char) => char.id === selection.existingCharacterId
          );
          if (existingChar) {
            allCharacters.push(existingChar);
          }
        }
      });

      // Create new characters if any
      if (newCharacters.length > 0) {
        if (!storyTemplate?.styleTemplateId) {
          throw new ApiError({
            type: 'error',
            error: createStoryPageMessages.styleTemplateRequired,
            code: 'BAD_REQUEST',
            message: createStoryPageMessages.styleTemplateRequired,
          });
        }
        const result = await createCharacters(
          {
            characters: newCharacters,
          },
          storyTemplate.styleTemplateId
        );
        allCharacters.push(...result.characters);
      }

      setStoryCharacters(allCharacters);
      handleNext();
    } catch (error) {
      const apiError =
        error instanceof ApiError ? error : UNKNOWN_ERROR_API_ERROR;
      toast.error(apiError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateStory = async (formData: DedicationFormValues) => {
    if (!storyTemplate || !templateId) return;

    setIsSubmitting(true);

    try {
      // Create character assignments mapping characters to roles
      const characterAssignments: CharacterAssignment[] = storyCharacters.map(
        (character, index) => ({
          role_template_id: storyTemplate.story_template_roles[index].id,
          character_id: character.id,
        })
      );

      // Create the generated story
      const result = await createGeneratedStory({
        story_template_id: templateId,
        title: storyTemplate.title,
        dedication: formData.dedication || null,
        character_assignments: characterAssignments,
      });

      setGeneratedStory(result.story);
      toast.success(createStoryPageMessages.storyCreatedSuccess);
      handleNext();
    } catch (error) {
      const apiError =
        error instanceof ApiError ? error : UNKNOWN_ERROR_API_ERROR;
      toast.error(apiError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToCheckout = () => {
    if (!generatedStory) {
      toast.error(createStoryPageMessages.storyNotFound);
      return;
    }
    navigate(buildRoute.checkout(generatedStory.id));
  };

  // Show spinner while loading template
  if (loadingTemplate) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CharacterForm
            storyRoles={storyTemplate?.story_template_roles || null}
            formData={formData}
            onFormDataUpdate={handleFormDataUpdate}
            onSubmit={handleCharacterSubmit}
            isSubmitting={isSubmitting}
            existingCharacters={existingCharacters}
          />
        );
      case 2:
        return (
          <CharacterPreviewStep
            characters={storyCharacters}
            isLoading={false}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case 3:
        return (
          <DedicationForm
            onSubmit={handleCreateStory}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        );
      case 4:
        return (
          <StoryPreviewStep
            generatedStory={generatedStory}
            onBack={handleBack}
            onNext={goToCheckout}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return createStoryPageMessages.step1Title;
      case 2:
        return createStoryPageMessages.step2Title;
      case 3:
        return createStoryPageMessages.step3Title;
      case 4:
        return createStoryPageMessages.step4Title;
      default:
        return '';
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="mb-1 flex justify-center">
        <img
          src="/logo/wfsc-logo-with-flair-xlong.png"
          alt=""
          className="h-18 w-auto"
        />
      </div>
      {/* Header */}
      <div className="mb-8">
        <div className="mb-4 flex items-center">
          <Heading2>{getStepTitle()}</Heading2>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <Small>
            {createStoryPageMessages.stepIndicator(currentStep, TOTAL_STEPS)}:{' '}
            {getStepTitle()}
          </Small>
          <div className="mt-4 h-2 w-full rounded-full bg-stone-200">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep - 1) / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step Content */}
      {/* for simple form submit button handling, each component renders its own navigation buttons */}
      <div className="rounded-lg border border-stone-200 bg-white p-8 shadow-sm">
        {renderCurrentStep()}
      </div>
    </div>
  );
}
